import * as React from "react";
import * as ReactQuery from "react-query";
import { UseMutationResult } from "react-query";
import { OtpAnalyticsInterface } from "../../one-time-password";

import { InitialStateMinimumRequirements_OneTimePassword } from "./one-time-password-intitial-state";

interface OneTimePasswordSendCodeConsumerInterface {
  sendCode: (
    email: string
  ) => Promise<OneTimePasswordSendCodeApi_Response | undefined>;
  data: UseMutationResult<OneTimePasswordSendCodeApi_GraphQlResponse>["data"];
}
const defaultValue = {} as unknown as OneTimePasswordSendCodeConsumerInterface;
const OneTimePasswordSendCodeContext = React.createContext(defaultValue);

interface OneTimePasswordSendCodeApi_InputProps {
  store_code: string;
  email: string;
}

export interface OneTimePasswordSendCodeApi_Response {
  wasSuccessful?: boolean;
  wasUnsuccessfulDueToUserExceedingMaxRetries?: boolean;
  retriesLeftForThisUser?: number;
}

interface OneTimePasswordSendCodeApi_GraphQlResponse {
  errors?: { message?: string }[];
  data?: {
    sendOtpCode?: OneTimePasswordSendCodeApi_Response;
  };
}

export interface OneTimePasswordSendCodeProviderProps {
  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_OneTimePassword;
  };
  useAnalytics: () => { analytics: Required<OtpAnalyticsInterface> };
}

export const OneTimePasswordSendCodeProvider: React.FunctionComponent<
  OneTimePasswordSendCodeProviderProps
> = (props) => {
  const { initialState } = props.useInitialState();
  const { analytics } = props.useAnalytics();

  const mutationKey = "one-time-password-send-code";

  const mutationFn = async (
    inputData: OneTimePasswordSendCodeApi_InputProps
  ) => {
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Store", inputData.store_code);

    const sendOtpCodeMutation = `mutation {
      sendOtpCode(
        email: "${inputData.email}"
      ) {
        wasSuccessful
        wasUnsuccessfulDueToUserExceedingMaxRetries
        retriesLeftForThisUser
      }
    }`;

    const request = new Request("/graphql", {
      method: "POST",
      headers,
      body: JSON.stringify({ query: sendOtpCodeMutation }),
    });

    const sendOtpCodeResponse = await fetch(request);

    const sendOtpCodeResponseJson =
      (await sendOtpCodeResponse?.json()) as OneTimePasswordSendCodeApi_GraphQlResponse;

    return sendOtpCodeResponseJson;
  };

  const mutation = ReactQuery.useMutation(mutationFn, {
    mutationKey,
  });

  const sendCode: OneTimePasswordSendCodeConsumerInterface["sendCode"] =
    React.useCallback(
      async (email) => {
        if (analytics.hasInitialized) {
          analytics.sendOtpCode({ email });
        }

        const oneTimePasswordSendCode_inputProps: OneTimePasswordSendCodeApi_InputProps =
          {
            store_code: initialState.store_code,
            email,
          };

        const thisSendCodeGraphqlResponse = await mutation.mutateAsync(
          oneTimePasswordSendCode_inputProps
        );

        if (thisSendCodeGraphqlResponse?.errors) {
          let errorMessage = "An unknown error has occurred.";

          if (Array.isArray(thisSendCodeGraphqlResponse?.errors)) {
            const firstError = thisSendCodeGraphqlResponse.errors[0];
            if (firstError && firstError.message) {
              errorMessage = firstError.message;
            }
          }

          throw new Error(errorMessage);
        }

        const thisSendCodeResponse =
          thisSendCodeGraphqlResponse?.data?.sendOtpCode;

        return thisSendCodeResponse;
      },
      [
        mutation,
        props,
        props.useInitialState,
        initialState,
        props.useAnalytics,
        analytics,
      ]
    );

  const value: OneTimePasswordSendCodeConsumerInterface = React.useMemo(
    () => ({
      sendCode,
      data: mutation.data,
    }),
    [sendCode]
  );
  return (
    <OneTimePasswordSendCodeContext.Provider value={value}>
      {props.children || []}
    </OneTimePasswordSendCodeContext.Provider>
  );
};

export const useOneTimePasswordSendCode = () => {
  const context = React.useContext(OneTimePasswordSendCodeContext);
  if (context === defaultValue) {
    throw new Error(
      "useOneTimePasswordSendCode must be used within a OneTimePasswordSendCodeProvider"
    );
  }
  return context;
};
